import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getReviewQuestions, getReviewStats, clearReviewData } from '../utils/reviewUtils';
import { FaTrashAlt, FaClock, FaChartBar } from 'react-icons/fa';

const ReviewMode = () => {
    const [reviewQuestions, setReviewQuestions] = useState([]);
    const [stats, setStats] = useState({});
    const [showClearConfirm, setShowClearConfirm] = useState(false);

    useEffect(() => {
        loadReviewData();
    }, []);

    const loadReviewData = () => {
        const questions = getReviewQuestions();
        const reviewStats = getReviewStats();
        setReviewQuestions(questions);
        setStats(reviewStats);
    };

    const handleClearReviewData = () => {
        clearReviewData();
        loadReviewData();
        setShowClearConfirm(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '未実施';
        const date = new Date(dateString);
        return date.toLocaleDateString('ja-JP');
    };

    const getCategoryDisplay = (question) => {
        if (question.category) {
            return question.category;
        }
        return `${question.part_of_speech} (${question.difficulty})`;
    };

    if (reviewQuestions.length === 0) {
        return (
            <div className="modern-container">
                <div className="modern-section text-center">
                    <div className="modern-alert modern-alert-info fade-in-up">
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '4rem', marginBottom: 'var(--spacing-lg)' }}>
                                🎯
                            </div>
                            <h3 style={{ marginBottom: 'var(--spacing-md)' }}>
                                復習する問題がありません！
                            </h3>
                            <p style={{ marginBottom: 'var(--spacing-lg)' }}>
                                クイズで間違えた問題が自動的にここに追加されます。
                            </p>
                            <Link to="/" className="modern-btn modern-btn-primary">
                                クイズを始める
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modern-container">
            <div className="modern-section">
                {/* Header */}
                <div className="text-center fade-in-up" style={{ marginBottom: 'var(--spacing-2xl)' }}>
                    <h2 style={{ 
                        background: 'var(--warning-gradient)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        marginBottom: 'var(--spacing-lg)'
                    }}>
                        📚 復習モード
                    </h2>
                </div>
                
                {/* 統計情報 */}
                <div className="modern-card fade-in-up" style={{ marginBottom: 'var(--spacing-2xl)' }}>
                    <div className="modern-card-header">
                        <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                            <FaChartBar />
                            復習統計
                        </h4>
                    </div>
                    <div className="modern-card-body">
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            flexWrap: 'wrap',
                            gap: 'var(--spacing-lg)'
                        }}>
                            <div style={{ flex: 1, minWidth: '200px' }}>
                                <p style={{ 
                                    fontSize: '1.1rem',
                                    marginBottom: 'var(--spacing-md)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--spacing-sm)'
                                }}>
                                    復習待ちの問題: 
                                    <span className="modern-badge modern-badge-warning">
                                        {stats.totalCount}問
                                    </span>
                                </p>
                                
                                <div>
                                    <strong style={{ color: 'var(--text-primary)' }}>カテゴリ別内訳:</strong>
                                    <div style={{ 
                                        marginTop: 'var(--spacing-sm)',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 'var(--spacing-sm)'
                                    }}>
                                        {Object.entries(stats.categoryBreakdown || {}).map(([category, count]) => (
                                            <span key={category} className="modern-badge modern-badge-info">
                                                {category}: {count}問
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div>
                                <button 
                                    className="modern-btn modern-btn-danger"
                                    onClick={() => setShowClearConfirm(true)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}
                                >
                                    <FaTrashAlt />
                                    データをクリア
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* クリア確認 */}
                {showClearConfirm && (
                    <div className="modern-alert modern-alert-danger fade-in-up" style={{ marginBottom: 'var(--spacing-2xl)' }}>
                        <div>
                            <strong>復習データをクリアしますか？</strong>
                            <div style={{ 
                                marginTop: 'var(--spacing-md)',
                                display: 'flex',
                                gap: 'var(--spacing-md)'
                            }}>
                                <button 
                                    className="modern-btn modern-btn-danger"
                                    onClick={handleClearReviewData}
                                >
                                    削除する
                                </button>
                                <button 
                                    className="modern-btn modern-btn-secondary"
                                    onClick={() => setShowClearConfirm(false)}
                                >
                                    キャンセル
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* 復習開始ボタン */}
                <div className="text-center fade-in-up" style={{ marginBottom: 'var(--spacing-2xl)' }}>
                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 'var(--spacing-md)',
                        flexWrap: 'wrap'
                    }}>
                        <Link 
                            to="/quiz/review/all/review" 
                            className="modern-btn modern-btn-success"
                            style={{ minWidth: '200px' }}
                        >
                            🚀 復習を開始する ({reviewQuestions.length}問)
                        </Link>
                        <Link 
                            to="/" 
                            className="modern-btn modern-btn-secondary"
                        >
                            ホームに戻る
                        </Link>
                    </div>
                </div>

                {/* 復習問題一覧 */}
                <div>
                    <h3 style={{ 
                        textAlign: 'center',
                        marginBottom: 'var(--spacing-2xl)',
                        color: 'var(--text-primary)'
                    }}>
                        復習問題一覧
                    </h3>
                    <div style={{ 
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gap: 'var(--spacing-lg)'
                    }}>
                        {reviewQuestions.map((question, index) => (
                            <div 
                                key={index} 
                                className="modern-card fade-in-up"
                                style={{ 
                                    animationDelay: `${index * 0.05}s`,
                                    height: 'fit-content'
                                }}
                            >
                                <div className="modern-card-body">
                                    <div style={{ 
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        marginBottom: 'var(--spacing-md)'
                                    }}>
                                        <h5 style={{ 
                                            color: 'var(--text-primary)',
                                            margin: 0,
                                            fontSize: '1.25rem'
                                        }}>
                                            {question.word}
                                        </h5>
                                        <span className="modern-badge modern-badge-info">
                                            {getCategoryDisplay(question)}
                                        </span>
                                    </div>
                                    
                                    <p style={{ 
                                        color: 'var(--text-secondary)',
                                        marginBottom: 'var(--spacing-md)'
                                    }}>
                                        <strong>意味:</strong> {question.japanese}
                                    </p>
                                    
                                    {question.example && (
                                        <p style={{ 
                                            color: 'var(--text-muted)',
                                            fontSize: '0.9rem',
                                            fontStyle: 'italic',
                                            marginBottom: 'var(--spacing-md)',
                                            padding: 'var(--spacing-sm)',
                                            backgroundColor: 'var(--background-primary)',
                                            borderRadius: 'var(--radius-md)',
                                            borderLeft: '3px solid var(--primary-color)'
                                        }}>
                                            <strong>例文:</strong> {question.example}
                                        </p>
                                    )}
                                    
                                    <div style={{ 
                                        fontSize: '0.875rem',
                                        color: 'var(--text-muted)',
                                        borderTop: '1px solid var(--border-color)',
                                        paddingTop: 'var(--spacing-md)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 'var(--spacing-xs)'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                                            <FaClock />
                                            追加日: {formatDate(question.addedAt)}
                                        </div>
                                        <div>
                                            復習回数: <span className="modern-badge modern-badge-warning" style={{ fontSize: '0.7rem' }}>
                                                {question.reviewCount}回
                                            </span>
                                        </div>
                                        <div>
                                            最終復習: {formatDate(question.lastReviewed)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewMode;